import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, RotateCcw, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  chatKnowledge,
  exportKnowledge,
  importKnowledgeSnapshot,
  type KnowledgeChatMessage,
  type KnowledgeEdge,
  type KnowledgeEntity,
  type KnowledgePendingMutation,
  type KnowledgeSnapshot,
} from '@/entities/knowledge';
import { ApiError } from '@/shared/api';
import { useI18n, type Messages } from '@/shared/i18n';
import { isPortrait } from '@/shared/lib/hooks/use-mobile';
import { cn } from '@/shared/lib/utils';
import { HeaderAction, PageHeader } from '@/widgets/page-header';
import { KnowledgeGraphCanvas, type KnowledgeGraphSelection } from '@/widgets/knowledge-graph';
import { KnowledgeChatPanel } from '@/widgets/knowledge-chat';
import { KnowledgeDetailsPanel } from '@/widgets/knowledge-details';
import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

type SideTab = 'chat' | 'details';

function snapshotFilename(): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `knowledge-snapshot-${stamp}.json`;
}

function downloadSnapshotFile(snapshot: KnowledgeSnapshot) {
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = snapshotFilename();
  anchor.click();
  URL.revokeObjectURL(url);
}

function parseSnapshotFile(
  raw: string,
  copy: Messages,
): {
  entities: KnowledgeEntity[];
  edges: KnowledgeEdge[];
} {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(copy.knowledge.invalidFile);
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error(copy.knowledge.notObject);
  }
  const body = parsed as Record<string, unknown>;
  if (!Array.isArray(body.entities) || body.entities.length === 0) {
    throw new Error(copy.knowledge.emptyEntities);
  }
  const edges = Array.isArray(body.edges) ? (body.edges as KnowledgeEdge[]) : [];
  return {
    entities: body.entities as KnowledgeEntity[],
    edges,
  };
}

export function KnowledgePage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const restoreInputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<KnowledgeGraphSelection | null>(null);
  const [sideTab, setSideTab] = useState<SideTab>('chat');
  const [mobilePane, setMobilePane] = useState<'graph' | 'side'>(() =>
    isPortrait() ? 'graph' : 'side',
  );
  const [messages, setMessages] = useState<KnowledgeChatMessage[]>([]);
  const [pending, setPending] = useState<KnowledgePendingMutation[]>([]);
  const [draft, setDraft] = useState('');
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  const snapshotQuery = useQuery({
    queryKey: ['knowledge', 'export'],
    queryFn: exportKnowledge,
  });

  const selectedEntity = useMemo((): KnowledgeEntity | null => {
    if (!selected || !snapshotQuery.data) return null;
    return (
      snapshotQuery.data.entities.find(
        (entity) => entity.label === selected.label && entity.id === selected.id,
      ) ?? null
    );
  }, [selected, snapshotQuery.data]);

  const selectedEdges = useMemo(() => {
    if (!selected || !snapshotQuery.data) return [];
    return snapshotQuery.data.edges.filter(
      (edge) =>
        (edge.from_label === selected.label && edge.from_id === selected.id) ||
        (edge.to_label === selected.label && edge.to_id === selected.id),
    );
  }, [selected, snapshotQuery.data]);

  const handleSelect = (next: KnowledgeGraphSelection | null) => {
    setSelected(next);
    if (next) {
      setSideTab('details');
      setMobilePane('side');
    }
  };

  const chatMutation = useMutation({
    mutationFn: (input: { messages: KnowledgeChatMessage[]; allow_mutations?: boolean }) =>
      chatKnowledge(input),
    onSuccess: async (result) => {
      if (result.reply) {
        setMessages((prev) => [...prev, { role: 'assistant', content: result.reply }]);
      }
      setPending(result.pending_mutations ?? []);
      if (result.graph_changed) {
        await queryClient.invalidateQueries({ queryKey: ['knowledge', 'export'] });
        toast.success(t.knowledge.updated);
      }
      if (result.applied?.length) {
        toast.message(t.knowledge.applied(result.applied.length));
      }
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : t.knowledge.chatFailed;
      toast.error(message);
    },
  });

  const send = (content: string, allowMutations = false) => {
    const trimmed = content.trim();
    if (!trimmed || chatMutation.isPending) return;
    const nextMessages: KnowledgeChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setDraft('');
    if (!allowMutations) {
      setPending([]);
    }
    chatMutation.mutate({ messages: nextMessages, allow_mutations: allowMutations });
  };

  const handleBackup = async () => {
    if (snapshotBusy) return;
    setSnapshotBusy(true);
    try {
      const snapshot = snapshotQuery.data ?? (await exportKnowledge());
      downloadSnapshotFile(snapshot);
      toast.success(t.knowledge.downloaded(snapshot.total_entities ?? snapshot.entities.length));
    } catch (error) {
      const message = error instanceof ApiError ? error.message : t.knowledge.backupFailed;
      toast.error(message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const handleRestoreFile = async (file: File | undefined) => {
    if (!file || snapshotBusy) return;
    setSnapshotBusy(true);
    try {
      const raw = await file.text();
      const { entities, edges } = parseSnapshotFile(raw, t);
      const result = await importKnowledgeSnapshot({
        entities,
        edges,
        clear_first: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['knowledge', 'export'] });
      setSelected(null);
      toast.success(
        t.knowledge.restored(result.entities_upserted) +
          (result.cleared ? t.knowledge.cleared(result.cleared) : ''),
      );
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : t.knowledge.restoreFailed;
      toast.error(message);
    } finally {
      setSnapshotBusy(false);
      if (restoreInputRef.current) {
        restoreInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={t.knowledge.title}
        description={t.knowledge.description}
        actions={
          <>
            <input
              ref={restoreInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(event) => {
                void handleRestoreFile(event.target.files?.[0]);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={snapshotBusy}
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label={t.knowledge.snapshot}
                    title={t.knowledge.snapshot}
                  />
                }
              >
                <Download className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-40">
                <DropdownMenuItem
                  disabled={snapshotBusy}
                  onClick={() => {
                    void handleBackup();
                  }}
                >
                  <Download className="size-3.5" />
                  {t.knowledge.backup}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={snapshotBusy}
                  onClick={() => restoreInputRef.current?.click()}
                >
                  <Upload className="size-3.5" />
                  {t.knowledge.restore}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <HeaderAction
              label={t.knowledge.refresh}
              variant="outline"
              onClick={() => snapshotQuery.refetch()}
              disabled={snapshotQuery.isFetching || snapshotBusy}
            >
              <RotateCcw className="size-4" />
            </HeaderAction>
          </>
        }
        sidebarOpen={mobilePane === 'side'}
        onToggleSidebar={() => setMobilePane((pane) => (pane === 'side' ? 'graph' : 'side'))}
      />

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="min-h-0 min-w-0 flex-1">
          {snapshotQuery.isError ? (
            <div className="flex h-full items-center justify-center text-sm text-destructive">
              {t.knowledge.loadFailed}
            </div>
          ) : (
            <KnowledgeGraphCanvas
              snapshot={snapshotQuery.data}
              selected={selected}
              onSelect={handleSelect}
            />
          )}
        </div>

        <aside
          className={cn(
            'min-h-0 w-full min-w-0 flex-col border-l bg-background',
            mobilePane === 'side'
              ? 'absolute inset-0 z-20 flex desktop:static desktop:z-auto desktop:w-96 desktop:max-w-[40%] desktop:shrink'
              : 'hidden',
          )}
        >
          <Tabs
            value={sideTab}
            onValueChange={(value) => {
              if (value === 'chat' || value === 'details') setSideTab(value);
            }}
            className="flex h-full min-h-0 flex-col gap-0"
          >
            <div className="shrink-0 border-b px-3 pt-3">
              <TabsList variant="line" className="w-full">
                <TabsTrigger value="chat" className="flex-1 after:bottom-0">
                  {t.knowledge.chat}
                </TabsTrigger>
                <TabsTrigger value="details" className="flex-1 after:bottom-0">
                  {t.knowledge.details}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="chat"
              className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
            >
              <KnowledgeChatPanel
                messages={messages}
                pending={pending}
                draft={draft}
                busy={chatMutation.isPending}
                onDraftChange={setDraft}
                onSend={() => send(draft, false)}
                onConfirm={() => send(t.knowledge.confirmPrompt, true)}
                onReject={() => {
                  setPending([]);
                  setMessages((prev) => [
                    ...prev,
                    { role: 'user', content: t.knowledge.rejectPrompt },
                    { role: 'assistant', content: t.knowledge.rejectedReply },
                  ]);
                }}
              />
            </TabsContent>

            <TabsContent
              value="details"
              className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
            >
              <KnowledgeDetailsPanel entity={selectedEntity} edges={selectedEdges} />
            </TabsContent>
          </Tabs>
        </aside>
      </div>
    </div>
  );
}
